import React from 'react';
import { elementStyleFrom, ISearchElementStyle } from '../Helpers/searchPageConfig';

// Per-search-bar autocomplete configuration.
//
// Mirrors ac-main's web/src/components/Storefront/Editor/autocompleteConfig.ts. These configs are
// authored in the admin editor and shipped to the storefront as JSON inside headerConfig /
// searchPageConfig / homePageConfig, so the two copies must agree on field names — there is no shared
// package between the repos, same as every other *Config in this app.
//
// The storefront has one master switch (settings.enableSearchAutocomplete) deciding whether
// suggestions exist at all; this block decides, per bar, whether that bar shows them.
//
// Every style part is an ISearchElementStyle rendered through `elementStyleFrom`, exactly like the
// search/product/cart page configs — which is also what the admin editor authors, so `fontSize` is a
// PERCENT, `font` is the family, borders get their `solid` synthesized, and `$styleRef` presets and
// palette tokens resolve. (These were previously spread raw as inline CSS, which silently mis-read
// every one of those: a percent came out as px and a `font` family was dropped as an invalid shorthand.)

export interface IAutocompleteColumns {
    keywords?: boolean;
    manufacturers?: boolean;
    tags?: boolean;
    products?: boolean;
}

export interface IAutocompleteConfig {
    enabled?: boolean;
    columns?: IAutocompleteColumns;
    panel?: ISearchElementStyle;
    heading?: ISearchElementStyle;
    item?: ISearchElementStyle;
    itemActive?: ISearchElementStyle;
    product?: ISearchElementStyle;
    thumbnail?: ISearchElementStyle;
    count?: ISearchElementStyle;
}

/** Every part of the popout, resolved once into styles that can be spread onto an element. */
export interface IResolvedAutocompleteStyles {
    /** Panel style plus the custom properties the class-driven parts read. Spread onto the surface. */
    panel: React.CSSProperties;
    heading: React.CSSProperties;
    item: React.CSSProperties;
    product: React.CSSProperties;
    thumbnail: React.CSSProperties;
    count: React.CSSProperties;
}

/**
 * Whether a column is shown. Undefined means "not configured", which reads as ON — only an explicit
 * false hides a column, so a bar switched on without touching columns gets the full popout.
 */
export function columnEnabled(columns: IAutocompleteColumns | undefined, key: keyof IAutocompleteColumns): boolean {
    return columns?.[key] !== false;
}

/**
 * The columns the BFF answers out of its own cache, with no upstream call — everything except the
 * product cards, whose prices and stock must be re-checked per shopper on every request.
 */
const TEXT_COLUMNS: (keyof IAutocompleteColumns)[] = ['keywords', 'manufacturers', 'tags'];

/** The `columns` parameter naming the product column, which is requested separately. */
export const PRODUCTS_COLUMN = 'products';

/**
 * The `columns` parameter for the cheap half of the popout.
 *
 * Empty when the bar shows none of the text columns — and the caller must then skip that request
 * altogether rather than send an empty value, because the BFF reads an empty or unrecognized column
 * list as "every column", which would drag the product hydration back onto the request meant to avoid
 * it. (That fallback is deliberate on the BFF side: a caller predating the parameter must still get a
 * full popout.)
 */
export function textColumnsParam(columns: IAutocompleteColumns | undefined): string {
    return TEXT_COLUMNS.filter(k => columnEnabled(columns, k)).join(',');
}

/**
 * Resolves every style part once, through the same pipeline the rest of the page configs use.
 *
 * The `part` argument on each call is the slot a `$styleRef` preset is read from, so a named style
 * applies its heading part to the column headings and its product part to the mini-cards rather than
 * flattening everything to `base`.
 *
 * The highlighted row cannot be styled inline — there is no inline `:hover`, and the keyboard highlight
 * is class-driven — so `itemActive` is published as custom properties on the panel for index.css to
 * read. It is resolved first, so a preset or palette token works there too. Only background and text
 * colour are exposed, which is enough to keep the highlight legible on any panel.
 */
export function resolveAutocompleteStyles(config?: IAutocompleteConfig): IResolvedAutocompleteStyles {
    const panel = elementStyleFrom(config?.panel);
    const active = elementStyleFrom(config?.itemActive);

    const vars: Record<string, string> = {};
    if (active.backgroundColor) vars['--sf-suggest-active-bg'] = String(active.backgroundColor);
    if (active.color) vars['--sf-suggest-active-color'] = String(active.color);
    // Doubles as the column-divider and thumbnail-frame colour, so a themed panel border carries
    // through to the parts that have no dict of their own.
    if (panel.borderColor) vars['--sf-suggest-border'] = String(panel.borderColor);

    return {
        panel: { ...panel, ...(vars as React.CSSProperties) },
        heading: elementStyleFrom(config?.heading, 'heading'),
        item: elementStyleFrom(config?.item),
        product: elementStyleFrom(config?.product, 'product'),
        thumbnail: elementStyleFrom(config?.thumbnail, 'image'),
        count: elementStyleFrom(config?.count, 'label'),
    };
}

/**
 * Parses a per-context config blob and pulls out its autocomplete block, tolerating malformed JSON the
 * way the other config parsers in this app do — a bad blob must degrade to "no popout", never throw
 * inside a render.
 */
export function parseAutocompleteConfig(configJson?: string): IAutocompleteConfig | undefined {
    if (!configJson) return undefined;
    try {
        const parsed = JSON.parse(configJson) as { searchAutocomplete?: IAutocompleteConfig };
        return parsed?.searchAutocomplete;
    } catch {
        return undefined;
    }
}
